import { useMemo } from 'react';
import { MilkdownProvider, Milkdown, useEditor } from '@milkdown/react';
import { Editor, defaultValueCtx, rootCtx } from '@milkdown/core';
import { nord } from '@milkdown/theme-nord';
import { commonmark } from '@milkdown/preset-commonmark';
import { gfm } from '@milkdown/preset-gfm';
import './MilkdownRenderer.css';

export interface MilkdownRendererProps {
  content: string;
  variant?: 'default' | 'compact' | 'resume-preview';
  className?: string;
  customStyles?: string;
}

/**
 * Inner editor component that uses the useEditor hook
 */
function MilkdownEditorInner({ content }: { content: string }) {
  useEditor((root) => {
    return Editor.make()
      .config(nord)
      .config((ctx) => {
        ctx.set(rootCtx, root);
        ctx.set(defaultValueCtx, content);
      })
      .use(commonmark)
      .use(gfm); // GFM includes table support
  }, [content]);

  return <Milkdown />;
}

/**
 * Centralized markdown renderer with full GitHub Flavored Markdown support
 * Uses Milkdown's React API with GFM preset for proper table rendering
 *
 * Features:
 * - Tables, strikethrough, task lists, autolinks (full GFM support)
 * - Three display variants: default, compact, resume-preview
 * - Dark mode compatible with Mantine CSS variables
 * - Custom stylesheet injection for PDF-like preview styling
 * - Read-only rendering (no editing UI)
 *
 * Variants:
 * - default: Standard spacing (job descriptions, general display)
 * - compact: Tighter spacing and smaller font (resume display)
 * - resume-preview: PDF-like styling with 11pt font and white background
 */
export default function MilkdownRenderer({
  content,
  variant = 'default',
  className = '',
  customStyles,
}: MilkdownRendererProps) {
  const variantClass = useMemo(() => {
    switch (variant) {
      case 'compact':
        return 'milkdown-compact';
      case 'resume-preview':
        return 'milkdown-resume-preview';
      default:
        return 'milkdown-default';
    }
  }, [variant]);

  return (
    <div className={`milkdown-renderer ${variantClass} ${className}`}>
      {/* Inject custom stylesheet if provided (e.g., for resume previews) */}
      {customStyles && <style dangerouslySetInnerHTML={{ __html: customStyles }} />}

      {/* Milkdown editor with GFM support - wrapped in MilkdownProvider */}
      <MilkdownProvider>
        <MilkdownEditorInner content={content} />
      </MilkdownProvider>
    </div>
  );
}
